import React, { useEffect , useState } from "react";
import axios from 'axios';
import { Bar, Pie } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement, Title } from 'chart.js';

import "./StatsPage.css";
import { getUserInfo } from "../../services/User.js";
import { API_BASE_URL } from "../../Variables";

function StatsPage(){
  // Register necessary Chart.js components
  ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement, Title);
  const token = localStorage.getItem("token");
  const user = getUserInfo();
  const startYear = user.date_created.split("-")[0];
  const barOptions = {
    indexAxis: 'y', 
    plugins: { legend: { display: false } },
    scales: {
      x: {
        beginAtZero: true,
        ticks: { stepSize: 1 },

      }
    }
  };

  const [stats, setStats] = useState([]);
  const [statsEmpty, setStatsEmpty] = useState(false);
  const [selectedShelf, setSelectedShelf] = useState({ id: "All", name: "All" });
  const [shelves, setShelves] = useState([]);
  const [yearFilter, setYearFilter] = useState("All");
  const [error, setError] = useState(null);
  const [topGenresData, setTopGenresData] = useState(null);
  const [topAuthorsData, setTopAuthorsData] = useState(null);
  const [pageDistributionData, setPageDistributionData] = useState(null);

  /**
   * Handle year filter change
   * @param {*} event - The event object
   */
  const handleYearFilterChange = (event) => {
    setYearFilter(event.target.value);
  };

  /**
   * Handle shelf filter change
   * @param {*} event - The event object
   */
  const handleShelfChange = (event) => {
    const selectedId = event.target.value;
    if (selectedId === "All") {
      setSelectedShelf({ id: "All", name: "All" });
    } else {
      const selectedShelfObj = shelves.find(shelf => shelf.id.toString() === selectedId);
      if (selectedShelfObj) {
        setSelectedShelf({ id: selectedShelfObj.id, name: selectedShelfObj.name });
      }
    }
  };

  /**
   * Fetch user shelves
   */
  useEffect(() => {
    const fetchUserShelves = async () => {
      try {
        setError(null);
        const response = await axios.get(`${API_BASE_URL}/shelves`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        setShelves(response.data);
      }catch (error) {
        if(error.response.data.message){
          setError(error.response.data.message);
        }
        else{
          setError(error.message);
        }
        
      }
    };

    fetchUserShelves();
  }, [token]);

  /**
   * Fetch stats
   */
  useEffect(() => {
    const fetchStats = async () => {
      try {
        setError(null);
        const response = await axios.get(`${API_BASE_URL}/stats`, {
          headers: { 'Authorization': `Bearer ${token}` },
          params: {
            shelf: selectedShelf.id,
            year: yearFilter
          }
        });
        setStats(response.data);
      } catch (error) {
        if(error.response.data.message){
          setError(error.response.data.message);
        }
        else{
          setError(error.message);
        }
      }
    };

    fetchStats();
  }, [token, selectedShelf, yearFilter]);

  /**
   * Update chart data when stats change
   */
  useEffect(() => {

    const customColors = [
      '#A5C4D4', '#B5D3A6', '#F2D388', '#F7B7A3', '#D6A5C0', 
      '#C5D6A5', '#FFD1A1', '#A5D6D1', '#FFD89A', '#B9A7C9'
    ];

    if (!stats || (!stats.topGenres?.length && !stats.topAuthors?.length && stats.totalBooksRead === 0)
    ) {
      setStatsEmpty(true);
    } else {
      setStatsEmpty(false);

      setTopGenresData({
        labels: stats.topGenres?.map(genre => genre.name) || [],
        datasets: [
          {
            label: ' # of Books',
            data: stats.topGenres?.map(genre => genre.count) || [],
            backgroundColor: customColors.slice(0, stats.topGenres?.length || 10),
            borderRadius: 5,
          }
        ]
      });

      setTopAuthorsData({
        labels: stats.topAuthors?.map(author => author.name) || [],
        datasets: [
          {
            label: ' # of Books',
            data: stats.topAuthors?.map(author => author.count) || [],
            backgroundColor: customColors.slice(0, stats.topAuthors?.length || 10),
            borderRadius: 5,
          }
        ]
      });

      setPageDistributionData({
        labels: ['Below 200 Pages', '200-500 Pages', 'Above 500 Pages'],
        datasets: [
          {
            label: ' # of Books',
            data: [
              stats.pageDistribution?.below200 || 0,
              stats.pageDistribution?.between200and500 || 0,
              stats.pageDistribution?.above500 || 0
            ],
            backgroundColor: ['#A5C4D4', '#F2D388', '#B5D3A6'],
            borderRadius: 5,
            borderColor: '#FFF1EC'
          }
        ]
      });
    }
  }, [stats]);

  return (
    <div className="stats-page">
      {error ? <div className="error">{error}</div> : (
        <div className="stats">
          <div className="filters">
            <div className="filter">
              <label>Year:</label>
              <select value={yearFilter} onChange={handleYearFilterChange}>
                <option value="All">All Time</option>
                {startYear && Array.from({length: new Date().getFullYear() - startYear + 1}, (v, k) => k + parseInt(startYear)).map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>

            <div className="filter">
              <label>Shelf:</label>
              <select
                value={selectedShelf.id} onChange={handleShelfChange}>
                <option value="All">All Shelves</option>
                {shelves.map(shelf => (
                  <option key={shelf.id} value={shelf.id}> {shelf.name} </option>
                ))}
              </select>
            </div>
          </div>

          {statsEmpty ? 
            <div className="message">No books in shelves to view stats!</div> 
          : stats.totalBooksInShelf === 0? 
            <div className="message">No books in shelf to view stats!</div> 
          :(
            <div className="stats-content">
              <div className="totals">
                <div className="totals-stats">
                  <h3>Total Books Read: &nbsp;
                  <span className="totals-data">{stats.totalBooksRead}</span></h3>
                </div>

                <div className="totals-stats">
                  <h3>Total Books in {selectedShelf.name === "All" ? "Shelves" : selectedShelf.name}: &nbsp; 
                  <span className="totals-data">{stats.totalBooksInShelf}</span> </h3>
                </div>
              </div>

              <div className="charts">
                {stats.topGenres?.length > 0 && (
                  <div className="chart-container">
                    <h3>Top Genres</h3>
                    <Bar data={topGenresData} 
                      options={barOptions}
                      width={500}
                      height={300}/>
                  </div>
                )}
                {stats.topAuthors?.length > 0 && (
                  <div className="chart-container">
                    <h3>Top Authors</h3>
                    <Bar data={topAuthorsData} 
                      options={barOptions}
                      width={500}
                      height={300}/>
                  </div>
                )}
                {stats.pageDistribution && (
                  <div className="chart-container pie-chart">
                    <h3>Page Distribution</h3>
                    <Pie data={pageDistributionData} />
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default StatsPage;
